import { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
  Chip,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  type FormField,
  type FieldType,
  FIELD_TYPES_WITH_OPTIONS,
  getFieldTypeColor,
  getFieldTypeDisplayLabel,
} from './types';
import { FieldTypeSelector } from './FieldTypeSelector';
import { OptionsEditor } from './OptionsEditor';

interface FormFieldEditorProps {
  field: FormField;
  index: number;
  onUpdate: (field: FormField) => void;
  onDelete: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isDragging?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
}

export const FormFieldEditor = ({
  field,
  onUpdate,
  onDelete,
  onMoveUp,
  onMoveDown,
  isDragging = false,
  dragHandleProps,
}: FormFieldEditorProps) => {
  const [expanded, setExpanded] = useState(false);

  const handleTypeChange = (type: FieldType) => {
    const updated: FormField = {
      type,
      label: field.label,
      isRequired: field.isRequired,
    };
    if (FIELD_TYPES_WITH_OPTIONS.includes(type)) {
      updated.options = field.options || [];
    }
    if (type === 'EquipmentLookup') {
      updated.filter = field.filter || '{"type":"Other"}';
    }
    onUpdate(updated);
  };

  return (
    <Card
      sx={{
        mb: 2,
        opacity: isDragging ? 0.5 : 1,
        border: isDragging ? '2px dashed' : '1px solid',
        borderColor: isDragging ? 'primary.main' : 'divider',
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Box
            {...dragHandleProps}
            sx={{
              cursor: 'grab',
              display: 'flex',
              alignItems: 'center',
              '&:active': { cursor: 'grabbing' },
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <DragIndicatorIcon color="action" />
          </Box>

          <Chip
            label={getFieldTypeDisplayLabel(field.type)}
            size="small"
            color={getFieldTypeColor(field.type)}
            variant="outlined"
            onClick={(e) => e.stopPropagation()}
          />

          <Typography
            variant="body1"
            sx={{ flexGrow: 1, fontWeight: field.isRequired ? 'bold' : 'normal' }}
          >
            {field.label || '(No label)'}
          </Typography>

          {field.isRequired && (
            <Chip label="Required" size="small" color="error" variant="outlined" />
          )}

          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>

          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm('Are you sure you want to delete this field?')) {
                onDelete();
              }
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>

        {expanded && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FieldTypeSelector value={field.type} onChange={handleTypeChange} />

              <TextField
                fullWidth
                label="Label"
                value={field.label}
                onChange={(e) => onUpdate({ ...field, label: e.target.value })}
                required
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.isRequired || false}
                    onChange={(e) =>
                      onUpdate({ ...field, isRequired: e.target.checked })
                    }
                  />
                }
                label="Required"
              />

              {(FIELD_TYPES_WITH_OPTIONS.includes(field.type) ||
                (field.type as string) === 'HtmlDisplay') && (
                <OptionsEditor
                  fieldType={field.type}
                  options={field.options || []}
                  onChange={(options) => onUpdate({ ...field, options })}
                />
              )}

              {(onMoveUp || onMoveDown) && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {onMoveUp && (
                    <IconButton onClick={onMoveUp} size="small">
                      ↑ Move Up
                    </IconButton>
                  )}
                  {onMoveDown && (
                    <IconButton onClick={onMoveDown} size="small">
                      ↓ Move Down
                    </IconButton>
                  )}
                </Box>
              )}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};
